import React from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Stack,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  SupportedLanguages,
  SupportedLanguagesByCode,
} from '@eluve/language-utils';

type LanguageSelectorComboBoxProps = {
  selectedLanguageCode: string;
  onSelectedLanguageCode: (languageCode: string) => Promise<void>;
  disabled?: boolean;
  preferredLanguages?: string[];
  selectButtonStyles?: string;
  variant?: 'default' | 'subtle' | 'solid';
  showLanguageIcon?: boolean;
  ariaLabelPrefix?: string;
};

export const LanguageSelectorComboBox: React.FC<
  LanguageSelectorComboBoxProps
> = ({
  selectedLanguageCode,
  onSelectedLanguageCode,
  disabled = false,
  preferredLanguages = [],
  selectButtonStyles = '',
  variant,
  showLanguageIcon = true,
  ariaLabelPrefix = 'Language',
}) => {
  const { t } = useTranslation();
  const sortedLanguages = Object.entries(SupportedLanguages).sort(
    ([, codeA], [, codeB]) => {
      const isCodeAPreferred = preferredLanguages.includes(codeA) ? 0 : 1;
      const isCodeBPreferred = preferredLanguages.includes(codeB) ? 0 : 1;
      return isCodeAPreferred - isCodeBPreferred;
    },
  );

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton
        className={selectButtonStyles}
        variant={variant}
        aria-label={`${ariaLabelPrefix} Selector`}
        dropdownIcon={
          <Icon name="ChevronDown" className="ml-2 shrink-0 opacity-50" />
        }
      >
        <Stack className="overflow-hidden" direction="horizontal" gap={2}>
          {showLanguageIcon && <Icon name="Languages" />}
          {SupportedLanguagesByCode[selectedLanguageCode]}
        </Stack>
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder={t('blocks.language.searchPlaceholder')}
        className="min-w-52"
        aria-label={`${ariaLabelPrefix} Dropdown`}
      >
        {sortedLanguages.map(([languageName, availableLanguageCode]) => (
          <ComboboxOption
            key={availableLanguageCode}
            onSelect={async () => {
              await onSelectedLanguageCode(availableLanguageCode);
            }}
          >
            <ComboboxSelectCheck
              className="mr-3"
              selected={availableLanguageCode === selectedLanguageCode}
            />
            {languageName}
          </ComboboxOption>
        ))}
      </ComboboxDropdown>
    </Combobox>
  );
};
